// src/App.js
import { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Carousel from "bootstrap/js/dist/carousel";

const API_BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

function App() {
  const styles = `
    /* Define a modern color palette and font */
    :root {
      --brand-start: #6a11cb;
      --brand-end: #2575fc;
      --brand-gradient: linear-gradient(90deg, var(--brand-start), var(--brand-end));
      --dark-bg: #0b0f19;
      --light-text: #e9f1fb;
      --muted-text: #a9b8d0;
      --border-color: rgba(255, 255, 255, 0.15);
      --input-bg: rgba(15, 22, 39, 0.7);
      --error: #ff7b86;
    }

    /* Background carousel: Darken images slightly for better text contrast */
    .bg-carousel { position: fixed; inset: 0; z-index: -2; }
    .bg-carousel .carousel, .bg-carousel .carousel-inner, .bg-carousel .carousel-item { height: 100%; }
    .bg-carousel .carousel-item { display: flex; align-items: center; justify-content: center; }
    .bg-carousel .carousel-item img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      filter: brightness(0.9) saturate(1.1) contrast(1.1);
    }

    /* Scrim: A deeper, more uniform dark overlay */
    .bg-scrim {
      position: fixed;
      inset: 0;
      z-index: -1;
      background: radial-gradient(ellipse at center, rgba(230, 238, 230, 0.6), rgba(11, 15, 25, 0.95));
      pointer-events: none;
    }

    /* Center the form */
    .auth-center {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }

    /* Glass card: Refined with softer shadow and corners */
    .wrapper {
      width: 100%;
      max-width: 480px;
      background: rgba(9, 13, 19, 0.5); /* Darker, less transparent glass */
      border: 1px solid var(--border-color);
      border-radius: 20px; /* Softer corners */
      padding: 32px;
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      color: var(--light-text);
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
      transition: all 0.3s ease;
    }

    /* Brand section: More prominent logo and title */
    .brand-row {
      display: flex;
      flex-direction: column; /* Stack logo and title */
      align-items: center;
      gap: 8px;
      margin-bottom: 24px;
    }
    .brand-logo {
      width: 50px;
      height: 50px;
      border-radius: 14px;
      display: grid;
      place-items: center;
      background: var(--brand-gradient);
      color: white;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      font-size: 24px;
    }
    .brand-title {
      margin: 0;
      font-size: 26px;
      font-weight: 700;
      letter-spacing: .5px;
      text-shadow: 0 2px 5px rgba(0,0,0,0.4);
    }

    .error-message {
      color: var(--error);
      background: rgba(255, 123, 134, 0.1);
      border: 1px solid rgba(255, 123, 134, 0.3);
      border-radius: 8px;
      padding: 10px;
      text-align: center;
      font-size: .9rem;
      margin-bottom: 16px;
    }

    /* Inputs: Modern, clean design with focus transition */
    .input-box {
      position: relative;
      margin-bottom: 18px;
    }
    .input-box input {
      width: 100%;
      border: 1px solid var(--border-color);
      background: var(--input-bg);
      color: var(--light-text);
      border-radius: 12px;
      padding: 14px 48px 14px 16px;
      outline: none;
      font-size: 1rem;
      transition: border-color 0.3s ease, box-shadow 0.3s ease;
    }
    .input-box input::placeholder { color: var(--muted-text); }
    .input-box input:focus {
      border-color: var(--brand-end);
      box-shadow: 0 0 0 3px rgba(37, 117, 252, 0.3);
    }

    /* Icons inside inputs */
    .input-icon {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 20px;
      color: var(--muted-text);
      pointer-events: none;
    }

    /* Password toggle button: More integrated look */
    .toggle-visibility {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 36px;
      height: 36px;
      border-radius: 8px;
      border: 0;
      background: transparent;
      color: var(--muted-text);
      display: grid;
      place-items: center;
      cursor: pointer;
      transition: background-color 0.2s ease, color 0.2s ease;
    }
    .toggle-visibility:hover { background: rgba(255, 255, 255, 0.1); color: var(--light-text); }
    .toggle-visibility:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(37, 117, 252, 0.4); }

    /* Remember me & Forgot password section */
    .remember-forgot {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: .9rem;
      margin: 12px 0 20px;
      color: var(--muted-text);
    }
    .remember-forgot label { cursor: pointer; }
    .remember-forgot a {
      color: var(--light-text);
      text-decoration: none;
      border-bottom: 1px solid transparent;
      transition: border-color 0.3s;
    }
    .remember-forgot a:hover {
      border-bottom-color: var(--brand-end);
      color: white;
    }
    .remember-forgot input[type="checkbox"] {
      margin-right: 8px;
      accent-color: var(--brand-end);
    }

    /* Login Button: Gradient with interactive hover effect */
    .btn-login {
      width: 100%;
      border-radius: 12px;
      padding: 14px;
      font-weight: 600;
      font-size: 1rem;
      color: white;
      background: var(--brand-gradient);
      border: none;
      box-shadow: 0 8px 20px rgba(106, 17, 203, 0.35);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .btn-login:hover:not(:disabled) {
      transform: translateY(-3px);
      box-shadow: 0 12px 25px rgba(37, 117, 252, 0.4);
      color: white; /* Ensure text remains white for Bootstrap overrides */
    }
    .btn-login:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }

    /* Footer note */
    .auth-note {
      color: var(--muted-text);
      font-size: .8rem;
      text-align: center;
      margin-top: 16px;
    }

    /* Carousel indicators: More subtle styling */
    .carousel-indicators [data-bs-target] {
      width: 8px; height: 8px; border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.4);
      border: none;
      opacity: 0.7;
      transition: opacity 0.3s;
    }
    .carousel-indicators .active {
      background-color: #ffffff;
      opacity: 1;
    }

    /* Responsive adjustments */
    @media (max-width: 480px) {
      .wrapper { padding: 24px; }
    }
  `;

  const IMAGES = [
    "https://images.pexels.com/photos/3873643/pexels-photo-3873643.jpeg?auto=compress&cs=tinysrgb&w=1600",
    "https://images.pexels.com/photos/269790/pexels-photo-269790.jpeg?auto=compress&cs=tinysrgb&w=1600",
    "https://images.pexels.com/photos/108942/pexels-photo-108942.jpeg?auto=compress&cs=tinysrgb&w=1600",
  ];

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("rememberEmail") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(!!localStorage.getItem("rememberEmail"));
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);

  // 🔐 Visiting "/" acts as logout: clear auth-related keys
  useEffect(() => {
    const remembered = localStorage.getItem("rememberEmail");
    // Remove only auth/user keys (keep rememberEmail)
    ["token", "email", "name", "organization", "userId", "role", "expiresAt"].forEach((k) =>
      localStorage.removeItem(k)
    );
    if (remembered) localStorage.setItem("rememberEmail", remembered);
    // If you ever stored auth in sessionStorage, clear it too
    try {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("email");
    } catch {}
  }, []);

  // Auto-cycling carousel (no pause on hover)
  useEffect(() => {
    const el = document.getElementById("bgCarousel");
    if (!el) return;
    const existing = Carousel.getInstance(el);
    const instance =
      existing ||
      new Carousel(el, {
        interval: 4000,
        ride: "carousel",
        pause: false,
        wrap: true,
        touch: true,
        keyboard: false,
      });
    instance.cycle();
  }, []);

  const emailValid = /\S+@\S+\.\S+/.test(email);
  const passwordValid = password.length > 0;

  const loginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!emailValid || !passwordValid) {
      setLoginError("Please enter a valid email and password.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Invalid email or password");

      // Store token for route guard; store email if your app needs it later
      if (data?.token) localStorage.setItem("token", data.token);
      localStorage.setItem("email", email);

      if (remember) localStorage.setItem("rememberEmail", email);
      else localStorage.removeItem("rememberEmail");

      window.location.href = data?.redirect || "/Homepage";
    } catch (err) {
      setLoginError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>

      {/* Background carousel */}
      <div className="bg-carousel">
        <div
          id="bgCarousel"
          className="carousel slide carousel-fade h-100"
          data-bs-ride="carousel"
          data-bs-interval="4000"
          data-bs-pause="false"
          data-bs-touch="true"
          data-bs-wrap="true"
        >
          <div className="carousel-inner h-100">
            {IMAGES.map((src, i) => (
              <div key={i} className={`carousel-item h-100 ${i === 0 ? "active" : ""}`}>
                <img src={src} alt={`Slide ${i + 1}`} />
              </div>
            ))}
          </div>

          <div className="carousel-indicators">
            {IMAGES.map((_, i) => (
              <button
                key={i}
                type="button"
                data-bs-target="#bgCarousel"
                data-bs-slide-to={i}
                className={i === 0 ? "active" : ""}
                aria-current={i === 0 ? "true" : undefined}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Readability scrim */}
      <div className="bg-scrim" />

      {/* Centered login */}
      <main className="auth-center container-xxl">
        <div className="wrapper">
          <div className="brand-row">
            <div className="brand-logo">
              <i className="bi bi-map-fill"></i>
            </div>
            <h1 className="brand-title">Terra MapX</h1>
          </div>

          {loginError && <div className="error-message">{loginError}</div>}

          <form onSubmit={loginSubmit} noValidate>
            <div className="input-box">
              <input
                type="email"
                placeholder="Email address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                aria-label="Email address"
              />
              <i className="bi bi-person-circle input-icon" aria-hidden="true"></i>
            </div>

            <div className="input-box">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                aria-label="Password"
              />
              <button
                type="button"
                className="toggle-visibility"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((v) => !v)}
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>

            <div className="remember-forgot">
              <label className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember me
              </label>
              <a href="/ForgotPassword">Forgot password?</a>
            </div>

            <button type="submit" className="btn btn-primary btn-login" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>

          <div className="auth-note">Use your Terra account to continue.</div>
        </div>
      </main>
    </>
  );
}

export default App;